import type { Role } from '@sistemas/contracts'
import { CalendarDays, FolderKanban, Inbox, NotebookPen, type LucideIcon } from 'lucide-react'
import * as React from 'react'

import { canSeeNavItem, matchesNavItem, type NavItem } from '@/lib/navigation'

/**
 * Hub no celular (Onda 6.1). Abaixo de 768 px o shell troca a sidebar por uma
 * barra inferior com as quatro telas feitas para o celular; as outras pedem o
 * computador. O corte é o `md` do Tailwind: as classes `max-md:` e este hook
 * dizem a mesma coisa.
 */
export const MOBILE_QUERY = '(max-width: 767px)'

/** Endereço que a tela "feita para o computador" manda abrir. */
export const DESKTOP_HOST = 'sistemas.flexibase.com.br'

const TIME: readonly Role[] = ['platform_admin', 'gestor', 'lider', 'membro']

/** A barra inferior, na ordem do plano. Chamados segue a regra da sidebar. */
export const MOBILE_NAV: readonly (NavItem & { icon: LucideIcon })[] = [
  { to: '/caderno', label: 'Notas', icon: NotebookPen },
  { to: '/projetos', label: 'Projetos', icon: FolderKanban },
  { to: '/chamados', label: 'Chamados', icon: Inbox, requiresAnyRole: TIME },
  { to: '/agenda', label: 'Agenda', icon: CalendarDays },
]

export function visibleMobileNav(roles: readonly Role[]) {
  return MOBILE_NAV.filter((item) => canSeeNavItem(item, roles))
}

/**
 * Telas que funcionam no celular: as quatro da barra (com as sub-rotas de nota
 * e de quadro) e as notificações, aonde o sino leva. `/agenda/feriados` é
 * cadastro da gestão e fica no computador.
 */
export function isMobileRoute(pathname: string): boolean {
  if (pathname === '/agenda') return true
  return ['/caderno', '/projetos', '/chamados', '/notificacoes'].some((to) => matchesNavItem(pathname, to))
}

function subscribe(onChange: () => void) {
  if (typeof window === 'undefined' || !window.matchMedia) return () => undefined
  const query = window.matchMedia(MOBILE_QUERY)
  query.addEventListener('change', onChange)
  return () => query.removeEventListener('change', onChange)
}

function snapshot(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia(MOBILE_QUERY).matches
    : false
}

/** Largura de celular agora (e acompanha girar a tela ou redimensionar). */
export function useIsMobile(): boolean {
  return React.useSyncExternalStore(subscribe, snapshot, () => false)
}

/**
 * Quanto o teclado virtual cobre do fundo da tela, em px. O `visualViewport`
 * encolhe quando o teclado abre; a diferença para a janela é o teclado. É o que
 * põe a barra de formatação da nota logo acima dele.
 */
export function useKeyboardInset(active: boolean): number {
  const [inset, setInset] = React.useState(0)
  React.useEffect(() => {
    const viewport = typeof window === 'undefined' ? undefined : window.visualViewport
    if (!active || !viewport) {
      setInset(0)
      return
    }
    const update = () => {
      const covered = window.innerHeight - viewport.height - viewport.offsetTop
      setInset(covered > 1 ? Math.round(covered) : 0)
    }
    update()
    viewport.addEventListener('resize', update)
    viewport.addEventListener('scroll', update)
    return () => {
      viewport.removeEventListener('resize', update)
      viewport.removeEventListener('scroll', update)
    }
  }, [active])
  return inset
}
